/**
 * Pattern switcher — loads the other patterns of the same shape from the same
 * factory into the product page.
 *
 * Liquid on a product page cannot query products by tag, so the markup is
 * fetched from sections/pattern-switcher.liquid against a tag-filtered
 * collection URL. What comes back is Liquid-rendered server-side; only the
 * request is client-side.
 *
 * Fails silently and leaves the mount empty — the pattern family is a
 * navigational nicety, and a broken fetch must never disturb a product page.
 */
(() => {
  const mount = document.querySelector('[data-pattern-switcher-mount]');
  if (!mount || mount.dataset.loaded === 'true') return;

  const group = mount.dataset.patternGroup;
  const current = mount.dataset.currentHandle;
  if (!group) return;

  const url = `/collections/all/${encodeURIComponent(group)}?section_id=pattern-switcher`;

  /**
   * Only fetch once the switcher is near the viewport.
   *
   * It sits below the buy button, so a shopper who lands, reads the price
   * and adds to cart — or bounces — never scrolls to it. Fetching on load
   * made every product page pay ~47KB for the largest families whether or
   * not anyone looked. rootMargin starts the request a screen early so it
   * is already there by the time it scrolls in.
   */
  const start = () => {
    if (mount.dataset.loaded === 'true') return;
    mount.dataset.loaded = 'true';
    load();
  };

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        start();
      }
    }, { rootMargin: '600px 0px' });
    io.observe(mount);
  } else {
    start();
  }

  function load() {
  fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((html) => {
      // Parse only to TEST for content; the markup itself is injected with
      // innerHTML below.
      //
      // Images must NOT be created inside a detached DOMParser document and
      // then moved in: they begin decoding in that document and can keep a
      // stale, tiny intrinsic size afterwards (observed: a genuine 240x204
      // file reporting naturalWidth 88, rendering soft). Writing the markup
      // straight into the live document makes the browser create the images
      // in place and pick from the srcset properly.
      const probe = new DOMParser().parseFromString(html, 'text/html');
      if (!probe.querySelector('.brp-ps__item')) return; // group of one

      mount.innerHTML = html;

      // The section is rendered from a collection URL, so it cannot know which
      // product is being viewed. Mark it here, where we do.
      const mine = mount.querySelector(
        `[data-pattern-handle="${CSS.escape(current)}"]`
      );
      if (mine) mine.classList.add('is-current');

      track(mount, group, current);
    })
    .catch(() => {
      /* leave the mount empty */
    });
  }

  /**
   * Click tracking.
   *
   * Fires on BOTH channels, because they do different jobs:
   *
   *  - Shopify.analytics.publish is the native custom-event API. It makes
   *    the event available to Web Pixels (Meta, custom pixels, anything
   *    subscribing). NOTE: Shopify's own Analytics dashboard does NOT
   *    surface arbitrary custom events, so this alone gives no report.
   *  - gtag sends it to GA4, which is where the numbers are actually
   *    readable — and where the cart A/B's `ab_variant` user property
   *    already lives, so switcher use can be cut by variant.
   *
   * transport_type 'beacon' matters: the click navigates away immediately,
   * and a normal request would be cancelled in flight.
   *
   * One delegated listener on the mount, so it survives the injected
   * markup and costs nothing per swatch.
   */
  function track(root, groupTag, fromHandle) {
    root.addEventListener('click', (event) => {
      const link = event.target.closest('.brp-ps__link');
      if (!link) return;
      const item = link.closest('.brp-ps__item');
      if (!item) return;

      const items = [...root.querySelectorAll('.brp-ps__item')];
      const payload = {
        pattern_group: groupTag,
        from_handle: fromHandle,
        to_handle: item.dataset.patternHandle || '',
        to_pattern: (item.querySelector('.brp-ps__name')?.textContent || '').trim().slice(0, 90),
        position: items.indexOf(item) + 1,
        family_size: items.length,
        sold_out: item.classList.contains('is-soldout')
      };

      try {
        window.Shopify?.analytics?.publish?.('pattern_switcher_click', payload);
      } catch (e) { /* never block the navigation */ }

      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'pattern_switcher_click', {
            ...payload,
            transport_type: 'beacon'
          });
        }
      } catch (e) { /* never block the navigation */ }
      // deliberately no preventDefault — the link navigates as normal
    });
  }
})();
